import { Injectable } from "@angular/core";
import { resolveFgdsProductType } from "@domain/product/fgds-product-type-resolver";
import { FlameDetectionRange } from "@domain/product/flame-detection-range";
import {
  FgdsProductType,
  FullProductType,
  MatchType,
  Product,
  ProductProperty,
  ProductPropertyTemplate,
  ProductPropertyType,
  Relationship,
  RelationshipSubType,
  RelationshipType,
} from "@domain/product/product";
import { ProductCountryData } from "@domain/product/product-country-data";
import { ProductPriceData } from "@domain/product/product-price-data";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";

type PriceDataEntryInput = {
  type: string;
  price: number | string;
  currency: string;
  [key: string]: unknown;
};

const SAP_PRICE_PREFIX = "sap price";
const TEST_COUNTRY_CODE = "TEST";
const TEST_EFFECTIVE_COUNTRY = "DE";

@Injectable({
  providedIn: "root",
})
export class ProductMapperService {
  private countryCurrencyMapper = new CountryCurrencyMapper();

  mapProductAndProperties(productsJson: any, productPropertiesJson: any): Product[] {
    const propertyTemplates = this.mapPropertyTemplates(productPropertiesJson);

    return productsJson.map((productJson: any) => {
      const properties: ProductProperty[] = [];

      Object.entries<string>(productJson.properties.data).forEach(([key, value]) => {
        const template = propertyTemplates.get(key);
        if (!template) {
          console.warn("No property found for key " + key);
          return;
        }
        try {
          properties.push(template.createInstance(value));
        } catch (error) {
          console.error(`Error creating property from template. Type: ${template.type}, value: ${value}`, error);
        }
      });
      const id = productJson.id;
      const name = productJson.name.trim();
      const effectiveType = resolveFgdsProductType({
        materialNumber: productJson.id,
        type: productJson.type,
        subType: productJson.subType,
        fullType: productJson.typeFull,
      });
      let type = this.mapProductType(effectiveType ?? productJson.type);
      const fullTypeSource = effectiveType !== productJson.type ? effectiveType : productJson.typeFull;
      const fullType = this.mapFullType(fullTypeSource);
      if (fullType === FullProductType.FLAME_DETECTOR_T_FLAME_K) {
        type = FgdsProductType.FLAME_DETECTOR;
      }
      const isModular = productJson.isModular;
      const isAvailable = productJson.isAvailable;
      const cantoImageId = productJson.cantoImageId;
      const cantoIfuId = productJson.cantoIfuId;
      const cantoProductInfoId = productJson.cantoProductInfoId;
      const relationships = this.mapRelationships(productJson.relationships);
      const countryData: ProductCountryData[] = this.mapCountryData(productJson.country, productJson.priceData);
      const product = new Product(
        id,
        name,
        type,
        fullType,
        isModular,
        isAvailable,
        cantoImageId,
        cantoIfuId,
        cantoProductInfoId,
        relationships,
        properties,
        countryData,
      );
      product.parentFamilies = Array.isArray(productJson.parentFamilies) ? productJson.parentFamilies : [];
      product.subFamily = typeof productJson.subFamily === "string" ? productJson.subFamily : undefined;
      if (productJson.flameDetectionData) {
        product.flameDetectionData = (productJson.flameDetectionData as any[]).map((range) =>
          FlameDetectionRange.create(range.name, range.detectionRangeDefault, range.detectionRangeHigh, range.detectionRangeLow),
        );
      }
      return product;
    });
  }

  private mapPropertyTemplates(json: any): Map<string, ProductPropertyTemplate> {
    const properties: any[] = json;
    return new Map(
      properties.map((propJson) => {
        const productProperty = this.mapPropertyTemplate(propJson);
        return [productProperty.key, productProperty];
      }),
    );
  }

  private mapPropertyTemplate(json: any): ProductPropertyTemplate {
    let name = "";
    let info = undefined;
    let description = undefined;

    json.translation.forEach((t: any) => {
      if (t.translationType === "Criterium" || t.translationType === "Cluster") {
        name = t.translation;
      } else if (t.translationType === "CallOutInfoHead") {
        info = t.translation;
      } else if (t.translationType === "CallOutInfoText") {
        description = t.translation;
      }
    });

    return new ProductPropertyTemplate(json.propertyName, this.mapPropertyType(json.dataType), name, info, description);
  }

  private mapProductType(type: string): FgdsProductType {
    if (type.substring(0, 4) === "gwz-") {
      return FgdsProductType.GASWARNINGCENTER;
    }
    if (type.substring(0, 4) === "z-o-" || type.substring(0, 4) === "z-n-") {
      return FgdsProductType.ATTACHMENT;
    }

    switch (type) {
      case "transmitter":
        return FgdsProductType.TRANSMITTER;
      case "attachment":
      case "z-o":
      case "z-n":
        return FgdsProductType.ATTACHMENT;
      case "sensor":
        return FgdsProductType.SENSOR;
      case "gwz":
        return FgdsProductType.GASWARNINGCENTER;
      case "alarmDevice":
        return FgdsProductType.ALARMDEVICE;
      case "miscDevice":
        return FgdsProductType.MISCDEVICE;
      case "service":
        return FgdsProductType.SERVICE;
      case "warnSignLuminous":
        return FgdsProductType.SIGNALELEMENT;
      case "warnSignPlastic":
        return FgdsProductType.PLASTICSIGN;
      default:
        console.warn("Unknown product type " + type);
        return FgdsProductType.MISCDEVICE;
    }
  }

  private mapFullType(fullType: string): FullProductType {
    if (Object.values(FullProductType).includes(fullType as FullProductType)) {
      return fullType as FullProductType;
    }
    console.warn("Unknown full product type", fullType);
    return FullProductType.MISCDEVICE;
  }

  private mapPropertyType(type: string): ProductPropertyType {
    switch (type) {
      case "Bool":
        return ProductPropertyType.BOOLEAN;
      case "Text":
        return ProductPropertyType.TEXT;
      case "TemperatureRangeC":
        return ProductPropertyType.TEMPERATUR_RANGE_IN_CELSIUS;
      case "Number":
        return ProductPropertyType.NUMBER;
      case "NumberRange":
        return ProductPropertyType.NUMBER_RANGE;
      case "WeightKG":
        return ProductPropertyType.WEIGHT_IN_KG;
      case "DimensionsM":
        return ProductPropertyType.DIMENSIONS_IN_METER;
      default:
        console.warn("Unknown property dataType " + type);
        return ProductPropertyType.TEXT;
    }
  }

  mapCountryData(country: string, priceData: any): ProductCountryData[] {
    const countryData: ProductCountryData[] = [];
    const newCountryData = new ProductCountryData();
    newCountryData.countryCode = country;

    if (Array.isArray(priceData)) {
      const normalizedCountry = country.trim().toUpperCase();
      const effectiveCountry = normalizedCountry === TEST_COUNTRY_CODE ? TEST_EFFECTIVE_COUNTRY : normalizedCountry;
      const expectedCurrency = this.getExpectedCurrency(effectiveCountry);
      const sapPriceEntries = this.filterSapPriceEntries(priceData, expectedCurrency);

      for (const entry of sapPriceEntries) {
        const normalizedPrice = this.normalizePrice(entry.price);
        if (normalizedPrice !== undefined) {
          newCountryData.prices.push(new ProductPriceData(entry.type, normalizedPrice, this.normalizeCurrency(entry.currency)));
        }
      }
    }

    countryData.push(newCountryData);

    return countryData;
  }

  private getSapRegion(type: string): string {
    const normalized = type.trim();
    const lower = normalized.toLowerCase();

    if (!lower.startsWith(SAP_PRICE_PREFIX)) return "";

    const region = normalized.slice(SAP_PRICE_PREFIX.length).trim();

    return region.toUpperCase();
  }

  private isSapPriceType(type: string): boolean {
    return type.trim().toLowerCase().startsWith(SAP_PRICE_PREFIX);
  }

  private static readonly ZREF_PRICE_SUFFIX = "ZREF";

  private isZrefPriceType(type: string): boolean {
    return type.trim().toUpperCase().endsWith(ProductMapperService.ZREF_PRICE_SUFFIX);
  }

  private filterSapPriceEntries(priceData: unknown[], expectedCurrency?: string): PriceDataEntryInput[] {
    return priceData
      .filter((entry): entry is PriceDataEntryInput => this.isPriceDataEntry(entry))
      .filter((entry) => {
        if (!this.isSapPriceType(entry.type)) return false;
        // ZREF prices are global and should not be filtered by currency
        if (!this.isZrefPriceType(entry.type) && expectedCurrency && this.normalizeCurrency(entry.currency) !== expectedCurrency)
          return false;
        return this.normalizePrice(entry.price) !== undefined;
      });
  }

  private isPriceDataEntry(entry: unknown): entry is PriceDataEntryInput {
    if (!entry || typeof entry !== "object") return false;

    const record = entry as Record<string, unknown>;

    return (
      typeof record["type"] === "string" &&
      typeof record["currency"] === "string" &&
      (typeof record["price"] === "number" || typeof record["price"] === "string")
    );
  }

  private normalizeCurrency(currency: string): string {
    return currency.trim().toUpperCase();
  }

  private normalizePrice(price: number | string): number | undefined {
    const normalized = typeof price === "number" ? price : Number(price);
    return Number.isFinite(normalized) ? normalized : undefined;
  }

  private getExpectedCurrency(country: string): string | undefined {
    try {
      return this.countryCurrencyMapper.getCurrency(country).toUpperCase();
    } catch {
      return undefined;
    }
  }

  mapRelationships(json: any): Relationship[] {
    const relationships: Relationship[] = [];
    json.forEach((relationship: any) => {
      const relationshipType = this.mapRelationshipType(relationship.relationshipType);
      const matchType = this.mapMatchType(relationship.matchType);
      const relationshipSubType = this.mapRelationshipSubType(relationship.relationshipSubType);
      if (!relationshipType || !matchType) {
        return;
      }
      relationships.push(new Relationship(relationship.id, relationshipType, matchType, relationshipSubType));
    });
    return relationships;
  }

  private mapRelationshipType(type: string): RelationshipType | undefined {
    switch (type) {
      case "extra":
        return RelationshipType.EXTRA;
      case "extraGuide":
        return RelationshipType.EXTRAGUIDE;
      case "extraReq":
        return RelationshipType.EXTRAREQ;
      case "sensor":
        return RelationshipType.SENSOR;
      case "module":
        return RelationshipType.MODULE;
      default:
        console.warn("Unknown relationship type " + type);
        return undefined;
    }
  }

  private mapMatchType(type: string): MatchType | undefined {
    switch (type) {
      case "direct":
        return MatchType.DIRECT;
      case "sensortype":
        return MatchType.SENSORTYPE;
      default:
        console.warn("Unknown match type " + type);
        return undefined;
    }
  }

  private mapRelationshipSubType(type: string): RelationshipSubType | undefined {
    switch (type) {
      case "3000plus-input":
        return RelationshipSubType.INPUT;
      case "3000plus-slotcover":
        return RelationshipSubType.SLOT_COVER;
      case "3000plus-relais":
        return RelationshipSubType.RELAY;
      case "3000plus-gateway":
        return RelationshipSubType.GATEWAY;
      default:
        return undefined;
    }
  }
}
